import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Icon from "@mdi/react";
import { mdiPlusCircleOutline } from "@mdi/js";
import { Routes, Route, useLocation } from "react-router-dom";
import { MainLayout, ListSidebar } from "layouts";
import { Button, CircularProgress } from "@mui/material";
import styles from "./styles.module.scss";
import { clsx } from "@ismael1361/utils";
import { navigate, HandleError } from "shared/utils";
import { NotistackServices, WorkspaceServices, PublicWorkspaceDTO, PublicTeamMemberDTO } from "shared/services";
import { MEMBER_STATUS_MAP } from "./mockData";
import { ToolLibraryContent } from "./ToolLibraryContent";
import { ToolManagementContent } from "./ToolManagementContent";

/** Gera iniciais a partir do nome (até 2 letras). */
function getInitials(name: string): string {
	return name
		.split(/[\s-_]+/)
		.filter(Boolean)
		.map((w) => w[0].toUpperCase())
		.slice(0, 2)
		.join("");
}

export const ToolsPage: React.FC = () => {
	const [sidebarSearch, setSidebarSearch] = useState("");
	const location = useLocation();

	// Data state
	const [workspaces, setWorkspaces] = useState<PublicWorkspaceDTO[]>([]);
	const [selectedWorkspaceUid, setSelectedWorkspaceUid] = useState<string | null>(null);
	const [members, setMembers] = useState<PublicTeamMemberDTO[]>([]);
	const [loadingWorkspaces, setLoadingWorkspaces] = useState(true);
	const [loadingMembers, setLoadingMembers] = useState(false);

	const timeRef = useRef<NodeJS.Timeout>(undefined);

	// Extrai o toolId (memberId) da URL para destacar no sidebar
	const activeToolId = useMemo(() => {
		const match = location.pathname.match(/^\/tools\/([^/]+)/);
		return match?.[1] ?? null;
	}, [location.pathname]);

	// Carrega workspaces
	const loadWorkspaces = useCallback(() => {
		setLoadingWorkspaces(true);
		clearTimeout(timeRef.current);
		timeRef.current = setTimeout(async () => {
			try {
				const data = await WorkspaceServices.listWorkspaces();
				setWorkspaces(data);
				if (data.length > 0 && !selectedWorkspaceUid) {
					setSelectedWorkspaceUid(data[0].uid);
				}
			} catch (error) {
				NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao carregar workspaces");
			} finally {
				setLoadingWorkspaces(false);
			}
		}, 300);
	}, []);

	useEffect(() => {
		loadWorkspaces();
	}, [loadWorkspaces]);

	// Carrega membros (ferramentas contratadas) quando workspace muda
	useEffect(() => {
		if (!selectedWorkspaceUid) return;
		let cancelled = false;
		setLoadingMembers(true);

		const load = async () => {
			try {
				const data = await WorkspaceServices.getMembers(selectedWorkspaceUid);
				if (cancelled) return;
				setMembers(data);
			} catch (error) {
				if (!cancelled) {
					NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao carregar ferramentas");
				}
			} finally {
				if (!cancelled) setLoadingMembers(false);
			}
		};

		load();
		return () => {
			cancelled = true;
		};
	}, [selectedWorkspaceUid]);

	// Filtro do sidebar
	const filteredMembers = useMemo(() => {
		if (!sidebarSearch.trim()) return members;
		const term = sidebarSearch.toLowerCase();
		return members.filter((m) => {
			const mcpName = m.contractedMcp?.mcp?.name ?? m.mcp?.name ?? "";
			return m.name.toLowerCase().includes(term) || mcpName.toLowerCase().includes(term);
		});
	}, [sidebarSearch, members]);

	const sidebar = (
		<ListSidebar
			searchPlaceholder="Buscar ferramentas..."
			onSearch={setSidebarSearch}
			footer={
				<Button
					variant="outlined"
					color="primary"
					fullWidth
					className={styles.sidebarAddBtn}
					disableElevation
					onClick={() => navigate("/tools")}
					startIcon={
						<Icon
							path={mdiPlusCircleOutline}
							size={0.8}
						/>
					}
				>
					Adicionar ferramenta
				</Button>
			}
		>
			{loadingWorkspaces || loadingMembers ? (
				<div className={styles.sidebarLoading}>
					<CircularProgress size={24} />
				</div>
			) : filteredMembers.length === 0 ? (
				<div className={styles.sidebarEmpty}>
					<p>Nenhuma ferramenta contratada</p>
				</div>
			) : (
				filteredMembers.map((member) => {
					const mcpName = member.contractedMcp?.mcp?.name ?? member.mcp?.name ?? member.name;
					const category = member.contractedMcp?.mcp?.category ?? member.mcp?.category ?? "—";
					const statusInfo = !member.isActive
						? { label: "Inativo", className: "statusInactive" }
						: (MEMBER_STATUS_MAP[member.status] ?? { label: member.status, className: "statusActive" });

					return (
						<div
							key={member.id}
							className={clsx(styles.toolCard, activeToolId === String(member.id) && styles.toolCardActive)}
							onClick={() => navigate(`/tools/${member.id}`)}
						>
							<div className={styles.toolCardInfo}>
								<div className={styles.toolCardRow}>
									<span className={styles.toolCardName}>{mcpName}</span>
									<span className={clsx(styles.toolCardStatus, styles[statusInfo.className])}>{statusInfo.label}</span>
								</div>
								<p className={styles.toolCardDesc}>{category}</p>
							</div>
						</div>
					);
				})
			)}
		</ListSidebar>
	);

	return (
		<MainLayout sidebar={sidebar}>
			<Routes>
				<Route
					path="/:toolId"
					element={
						<ToolManagementContent
							members={members}
							workspaceUid={selectedWorkspaceUid}
							onUpdate={() => setSelectedWorkspaceUid((u) => u)}
						/>
					}
				/>
				<Route
					path="*"
					element={<ToolLibraryContent />}
				/>
			</Routes>
		</MainLayout>
	);
};
